import { useState } from 'react'
import { HandCoins, Plus } from 'lucide-react'
import { Link } from 'react-router'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useLoanMutations, useLoanTypes, useLoans } from '../api'
import { INTEREST_METHOD_LABELS, LOAN_STATUS_LABELS } from '../types'
import type { InterestMethod, LoanStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<LoanStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  DISBURSED: 'info',
  CLOSED: 'success',
  CANCELLED: 'neutral',
  WRITTEN_OFF: 'danger',
}

/** Every loan, and what is still owed on it. */
export function LoansPage() {
  useBreadcrumb()
  const loans = useLoans()
  const types = useLoanTypes()
  const { apply } = useLoanMutations()

  const [draft, setDraft] = useState<{
    employeeId: string; loanTypeId: string; principal: string
    tenorMonths: string; currency: string; firstInstalmentOn: string; purpose: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const chosenType = types.data?.find((type) => type.id === draft?.loanTypeId)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Loans & advances"
        subtitle="What has been lent, and what is still owed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                employeeId: '', loanTypeId: types.data?.[0]?.id ?? '', principal: '',
                tenorMonths: '12', currency: 'AED',
                firstInstalmentOn: '', purpose: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New application
          </Button>
        }
      />

      <Card>
        {loans.isPending && <Skeleton className="m-4 h-24" />}
        {loans.isError && <ErrorState error={loans.error} onRetry={() => void loans.refetch()} />}
        {loans.isSuccess && loans.data.length === 0 && (
          <EmptyState
            title="Nothing has been lent"
            description="A loan is applied for, approved, and then repaid through payroll as an ordinary deduction."
          />
        )}
        {loans.isSuccess && loans.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Type</th>
                  <th className="p-3 text-end font-medium">Borrowed</th>
                  <th className="p-3 text-end font-medium">Over</th>
                  <th className="p-3 text-end font-medium">Still owed</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loans.data.map((loan) => (
                  <tr key={loan.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <Link className="font-mono underline underline-offset-2" to={`/loans/${loan.id}`}>
                        {loan.reference}
                      </Link>
                    </td>
                    <td className="p-3">{loan.employeeName ?? loan.employeeCode}</td>
                    <td className="p-3 text-slate-600">{loan.loanTypeName}</td>
                    <td className="p-3 text-end">{loan.principal} {loan.currency}</td>
                    <td className="p-3 text-end text-slate-600">{loan.tenorMonths} months</td>
                    <td className="p-3 text-end font-medium">{loan.outstandingPrincipal}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={loan.status}
                        tone={STATUS_TONE[loan.status as LoanStatus] ?? 'neutral'}
                        label={LOAN_STATUS_LABELS[loan.status as LoanStatus] ?? loan.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <HandCoins className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        The instalment is deducted through the ordinary payroll element named on the loan type,
        so it appears on the payslip, in the journal and in the explain trace like anything
        else. A month payroll cannot cover leaves arrears rather than being skipped.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="Apply for a loan"
        description="Checked against the type's maximum and tenor when it is saved."
        submitLabel="Save as draft"
        loading={apply.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await apply.mutateAsync({
                employeeId: draft.employeeId,
                loanTypeId: draft.loanTypeId,
                principal: Number(draft.principal),
                currency: draft.currency,
                tenorMonths: Number(draft.tenorMonths),
                firstInstalmentOn: draft.firstInstalmentOn || null,
                purpose: draft.purpose || null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That application could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="loan-employee">Person</Label>
                <EmployeePicker
                  id="loan-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
              </div>
              <div>
                <Label htmlFor="loan-type">Loan type</Label>
                <Select
                  id="loan-type"
                  className="w-full"
                  value={draft.loanTypeId}
                  onChange={(event) => setDraft({ ...draft, loanTypeId: event.target.value })}
                >
                  <option value="">Choose a type…</option>
                  {types.data?.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="loan-principal">How much</Label>
                <Input
                  id="loan-principal" type="number" value={draft.principal}
                  onChange={(event) => setDraft({ ...draft, principal: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="loan-tenor">Over how many months</Label>
                <Input
                  id="loan-tenor" type="number" value={draft.tenorMonths}
                  onChange={(event) => setDraft({ ...draft, tenorMonths: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="loan-first">First instalment</Label>
                <DatePicker
                  id="loan-first" value={draft.firstInstalmentOn}
                  onChange={(event) =>
                    setDraft({ ...draft, firstInstalmentOn: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Left blank, it is a month after the application.
                </p>
              </div>
              <div>
                <Label htmlFor="loan-currency">Currency</Label>
                <Input
                  id="loan-currency" maxLength={3} value={draft.currency}
                  onChange={(event) =>
                    setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
                />
              </div>
            </div>

            {chosenType && (
              <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <strong>{chosenType.name}: </strong>
                {INTEREST_METHOD_LABELS[chosenType.interestMethod as InterestMethod]}
                {chosenType.interestRate != null && ` at ${chosenType.interestRate}%`}.
                {chosenType.maximumAmount != null && ` Up to ${chosenType.maximumAmount}.`}
                {chosenType.maximumTenorMonths != null &&
                  ` Repaid over at most ${chosenType.maximumTenorMonths} months.`}
              </p>
            )}

            <div>
              <Label htmlFor="loan-purpose">Purpose</Label>
              <Input
                id="loan-purpose" value={draft.purpose}
                onChange={(event) => setDraft({ ...draft, purpose: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
